import { chartColor, chartColorPinned } from '@/shared/lib/chart-theme';
import type { IncompletePeriods } from '@/shared/lib/incomplete-periods';
import { getWholeNumberAxis } from '@/shared/lib/whole-number-axis';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
  type ChartData,
  type ChartOptions,
} from 'chart.js';
import type { FC } from 'react';
import { Bar } from 'react-chartjs-2';
import { TooltipElement } from '../tooltip';
import { Typography } from '../typography';
import { ChartEmptyState } from './chart-empty-state';
import {
  incompletePeriodPlugin,
  useIncompletePeriodsOptions,
} from './incomplete-period-plugin';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface AdmissionsDischargesBarChartProps {
  data: ChartData<'bar'>;
  title?: string;
  tooltipMessage?: string;
  incompletePeriods?: IncompletePeriods;
  /** Tooltip header per category; falls back to the category label. */
  tooltipTitles?: (string | undefined)[];
  /** Show "No data available" under the header instead of the bars. */
  isEmpty?: boolean;
}

export const AdmissionsDischargesBarChart: FC<
  AdmissionsDischargesBarChartProps
> = ({
  data,
  title,
  tooltipMessage,
  incompletePeriods,
  tooltipTitles,
  isEmpty = false,
}) => {
  const incompletePeriodsOptions =
    useIncompletePeriodsOptions(incompletePeriods);
  const values = data.datasets
    .flatMap((d) => d.data)
    .filter((v): v is number => typeof v === 'number');

  // People counts: whole-number ticks, and the tallest bar stops at 75% of the
  // axis so the "Incomplete Month/Week" label (card 196) always has room above it.
  const { max: axisMax, stepSize } = getWholeNumberAxis(Math.max(0, ...values));

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: false,
      },
      incompletePeriods: incompletePeriodsOptions,
      tooltip: {
        displayColors: false,
        backgroundColor: chartColor('--color-neutral-1'),
        borderColor: chartColorPinned('--color-neutral-5', '#e1e1e1'),
        borderWidth: 1,
        bodyColor: chartColor('--color-neutral-8'),
        titleColor: chartColor('--color-neutral-9'),
        padding: 8,
        cornerRadius: 2,
        xAlign: 'center',
        yAlign: 'bottom',
        callbacks: {
          title: (tooltipItems) => {
            if (tooltipItems.length > 0) {
              const [item] = tooltipItems;
              return tooltipTitles?.[item.dataIndex] ?? item.label;
            }
            return '';
          },
          label: (tooltipItem) => {
            const datasetLabel = tooltipItem.dataset.label || '';
            const value = tooltipItem.parsed.y;
            return `${datasetLabel}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          drawTicks: false,
          color: chartColorPinned('--color-neutral-5', '#d4d4d4'),
        },
        border: {
          display: true,
          color: chartColorPinned('--color-neutral-5', '#d4d4d4'),
          dash: [3, 2],
        },
        ticks: {
          maxRotation: 0,
          color: chartColor('--color-neutral-8'),
          font: {
            size: 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        min: 0,
        max: axisMax,
        grid: {
          color: chartColorPinned('--color-neutral-5', '#d4d4d4'),
          drawTicks: false,
        },
        border: {
          display: false,
          color: chartColorPinned('--color-neutral-5', '#d4d4d4'),
          dash: [3, 2],
        },
        ticks: {
          color: chartColor('--color-neutral-8'),
          font: {
            size: 11,
          },
          stepSize,
          precision: 0,
        },
      },
    },
  };

  return (
    <div className="relative h-full w-full">
      <div className="absolute top-0 left-0 z-10 flex w-full items-center justify-between">
        <div className="flex items-center gap-x-2">
          {title && (
            <Typography
              as="h5"
              variant="h5"
              weight="medium"
              className="text-neutral-12"
            >
              {title}
            </Typography>
          )}
          {tooltipMessage && (
            <TooltipElement size={14} content={tooltipMessage} />
          )}
        </div>

        <div className="ml-auto flex items-center gap-3">
          {data.datasets.map((dataset, i) => (
            <div className="flex items-center gap-2" key={i}>
              <div
                className="h-2 w-2"
                style={{ backgroundColor: dataset.backgroundColor as string }}
              />
              <span className="text-neutral-8 text-xs">{dataset.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="h-full w-full pt-8">
        {isEmpty ? (
          <ChartEmptyState />
        ) : (
          <Bar
            data={data}
            options={options}
            plugins={[incompletePeriodPlugin]}
          />
        )}
      </div>
    </div>
  );
};
